import { Can } from "@/components/citadel";
import { useProject } from "@/hooks/use-project";
import { AddResourceLinkModal } from "@/modals/projects/add-resource-link-modal";
import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { type QueryKey, useQueryClient } from "@msuite/ui/query";
import React from "react";

/** Props Interface */
interface ProjectResourceLinksMenuProps {
	queryKey: QueryKey;
	projectId: string;
}

export function ProjectResourceLinksMenu({ queryKey, projectId }: ProjectResourceLinksMenuProps) {
	/** Hooks */
	const { t } = useTranslation();
	const disclosure = useDisclosure();
	const qc = useQueryClient();

	/** Query */
	// A resource link is part of the site's record, so the rule binds against this site rather than
	// the category: `update Project` also reaches only the sites its holder runs.
	const { project } = useProject({ projectId });

	/** Functions */
	async function refetch() {
		await qc.invalidateQueries({ queryKey });
	}

	/** Render */
	return (
		<React.Fragment>
			<Can
				action="update"
				subject="Project"
				resource={project ? { ...project } : undefined}
			>
				<Button
					variant="primary"
					icon={<Icon name="Plus" />}
					onClick={disclosure.onOpen}
					data-cy="add-resource-link-button"
				>
					{t("resource_links.add_resource_link")}
				</Button>
			</Can>
			{disclosure.isOpen && (
				<AddResourceLinkModal
					projectId={projectId}
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					onComplete={refetch}
				/>
			)}
		</React.Fragment>
	);
}
